import { useMemo, SetStateAction } from 'react'
import { MILESTONE_DISPLAY_NAME, DEFAULT_BUSINESS_UNIT } from '@OpsWb/constants'
import { EXCEPTION_STATUS } from '@ProdWb/constants'

import logger from '~/lib/logger'
import {
    ExceptionFailure,
    ExceptionFailuresSort,
    ExceptionFailureStatus,
    PartialExceptionFailureResults,
    useExceptionListsQuery,
    useSetExceptionFailureStatusMutation,
    ExceptionListsDocument,
} from '~/gql/graphql'
import { ExceptionTypeTagPropsType } from '../components/ExceptionTypeTag'

import { ExceptionFailureRecordType } from './types'
import {
    ExceptionTagMap,
    EXCEPTION_FAILURE_FILTER_TYPE,
    filterMapping,
} from './constants'

export const getFormattedFilters = (filtersForExceptions?: {
    [key: string]: (string | number)[]
}) => {
    const filters: {
        [key: string]: (string | number) | (string | number)[]
    } = {}

    const specialFilters = [
        filterMapping[EXCEPTION_FAILURE_FILTER_TYPE.MFG_DRI],
        filterMapping[EXCEPTION_FAILURE_FILTER_TYPE.CUSTOMER_MFG_DRI],
    ]

    if (filtersForExceptions) {
        for (const key in filtersForExceptions) {
            if (key === filterMapping[EXCEPTION_FAILURE_FILTER_TYPE.CAR]) {
                const correctiveActionRequestStatuses: string[] = []

                filtersForExceptions[
                    filterMapping[EXCEPTION_FAILURE_FILTER_TYPE.CAR]
                ].forEach(value => {
                    const tempArray = value.toString().split('-')
                    const [carStatus] = tempArray

                    if (!correctiveActionRequestStatuses.includes(carStatus)) {
                        correctiveActionRequestStatuses.push(carStatus)
                    }
                })
                filters['correctiveActionRequestStatus'] =
                    correctiveActionRequestStatuses
            } else {
                filters[key] =
                    filtersForExceptions[key].length === 1 &&
                    !specialFilters.includes(key)
                        ? filtersForExceptions[key].toString()
                        : filtersForExceptions[key]
            }
        }
        specialFilters.forEach(key => {
            filters[key] = (filters[key] as string[])?.map(value =>
                Number(value),
            )
        })
    }
    return filters
}

export const useExceptionsData = (
    limit: number,
    offset: number | null,
    sort?: string,
    filtersForExceptions?: {
        [x: string]: (string | number)[]
    },
) => {
    const variables: {
        limit: number | null
        offset: number | null
        sort: ExceptionFailuresSort | null
        filters: any
    } = {
        limit,
        offset: offset,
        sort: sort as ExceptionFailuresSort,
        filters: getFormattedFilters(filtersForExceptions),
    }

    return useExceptionListsQuery({
        variables: variables,
        fetchPolicy: 'network-only',
    })
}

export const ExceptionsFormat = (
    exceptionLine: ExceptionFailure,
): ExceptionFailureRecordType => {
    const {
        id,
        exceptionFailureState,
        name,
        jobName,
        salesOrderName,
        manufacturingPartnerName,
        manufacturingPartnerId,
        correctiveActionRequest,
        rfqNumber,
        type,
        action,
        assignee,
        project,
        latestNote,
        erpSaleOrderId,
        jobId,
        failureDiscoveredBy,
        creator,
        mpMfgDri,
        exceptionFailureParts,
        updatedAt,
        nonConformanceEmailSentOn,
        isFailure,
        isCustomerImpacted,
        jobComputedStatus,
        assignedEntity,
        milestoneType,
        customerMfgDri,
        correctiveActionRequestId,
        businessUnit,
    } = exceptionLine

    return {
        key: id,
        exceptionFailureState,
        name,
        rfqNumber,
        jobName,
        salesOrderName,
        manufacturingPartnerName,
        manufacturingPartnerId,
        correctiveActionRequest,
        type,
        assignee,
        action,
        project,
        mpMfgDri,
        erpSaleOrderId,
        jobId,
        exceptionFailureParts,
        failureDiscoveredBy,
        latestNote,
        creator,
        updatedAt,
        nonConformanceEmailSentOn,
        isFailure,
        isCustomerImpacted,
        jobStatus: jobComputedStatus,
        assignedEntity,
        milestoneType,
        customerMfgDri,
        correctiveActionRequestId,
        businessUnit: businessUnit ?? DEFAULT_BUSINESS_UNIT,
    }
}

export const useExceptionLineData = (
    exceptionLines?: PartialExceptionFailureResults,
) => {
    return useMemo(() => {
        const lines = exceptionLines?.items

        return (
            lines && lines.map(exceptionLine => ExceptionsFormat(exceptionLine))
        )
    }, [exceptionLines])
}

export function getExceptionTag(reportState: string) {
    return ExceptionTagMap[reportState]
}

export function MutationOperations() {
    const [changeExceptionStatus] = useSetExceptionFailureStatusMutation()

    return {
        setExceptionFailureStatus: async (
            variables: {
                limit: number
                offset?: number
                sort?: string
                filters?: {
                    [key: string]: (string | number) | (string | number)[]
                }
            },
            exceptionId: string,
            status: ExceptionFailureStatus,
        ) => {
            try {
                await changeExceptionStatus({
                    variables: {
                        id: exceptionId,
                        status,
                    },
                    refetchQueries: [
                        {
                            query: ExceptionListsDocument,
                            variables,
                        },
                    ],
                })
            } catch (err) {
                logger.exception(err, {
                    extra: {
                        info: 'changeActionStatus::Failed to setExceptionStatus for exceptionId:  ',
                        exceptionId,
                    },
                })
            }
        },
    }
}

export const showDisabledDropdown = (status?: string | null) => {
    return (
        status == EXCEPTION_STATUS.VOIDED_EXCEPTION ||
        status == EXCEPTION_STATUS.CLOSED_FAILURE
    )
}

export const escalationClick = (
    setExceptionData: (
        value: SetStateAction<ExceptionFailureRecordType | undefined>,
    ) => void,
    showModal: (value: SetStateAction<boolean>) => void,
    record: ExceptionFailureRecordType,
) => {
    setExceptionData(record)
    showModal(true)
}

export const getExceptionTypeTag = (tagDetails: ExceptionTypeTagPropsType) => {
    const { type, assignedTo, milestone } = tagDetails

    return `${
        assignedTo ? `${assignedTo.toUpperCase()}- ` : ''
    }${type?.toUpperCase()}${
        milestone
            ? `- ${
                  MILESTONE_DISPLAY_NAME[
                      milestone as keyof typeof MILESTONE_DISPLAY_NAME
                  ]
              }`
            : ''
    }`
}
